import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useFormik } from "formik";
import * as yup from "yup";
import {
  Button,
  FormControl,
  FormHelperText,
  Alert,
  Stack,
  Typography,
  PinInput,
  StepIndicator,
} from "@financeable-com-au/financeable-ui";
import { AuthCard } from "@components/AuthCard";
import {
  AUTH_REQUEST_KEYS,
  authV2MagicSend,
  authV2MagicVerify,
  authV2Selector,
  setPendingMfa,
  toAuthV2Error,
  toMfaEnrolmentStart,
} from "@/store/slices/authV2Slice";
import { useMagicOtpCoolDown } from "@/hooks";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { buildPath } from "@/utils/buildPath";
import { completeAuthNavigation } from "@/utils/standaloneConnect";

const AuthMagic = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authV2 = useSelector(authV2Selector);
  const verifyLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.magicVerify]);
  const sendLoading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.magicSend]);

  const email = searchParams.get("email") ?? authV2?.magicChallenge?.email ?? "";
  const { magicOtpCooldownActive, magicOtpCooldownRemainingSeconds } = useMagicOtpCoolDown(email);
  const magicOtpCooldownUnit = magicOtpCooldownRemainingSeconds === 1 ? "second" : "seconds";
  const invitationToken = searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";
  const autoSubmittedRef = useRef(false);

  const navigateToSignIn = () => {
    navigate(buildPath("/auth/sign-in"));
  };

  const submitMagicCode = async (nextEmail: string, nextCode: string[] | string) => {
    const codeString = Array.isArray(nextCode) ? nextCode.join("") : nextCode;

    const payload = {
      email: nextEmail.trim(),
      code: codeString.trim(),
      ...(invitationToken ? { invitationToken } : {}),
    };

    const result = await dispatch(authV2MagicVerify(payload));
    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);

      if (parsedError.code === "MFA_REQUIRED" && parsedError.mfa) {
        dispatch(setPendingMfa(parsedError.mfa));
        navigate(buildPath("/auth/mfa", invitationToken ? { invitationToken } : undefined), {
          state: {
            mfa: parsedError.mfa,
            email: nextEmail.trim(),
            magicCode: Array.isArray(nextCode) ? nextCode : nextCode.split(""),
          },
        });
        return;
      }

      // MFA enrolment required to proceed.
      const mfaEnrolmentStart = toMfaEnrolmentStart(result.payload);
      if (parsedError.code === "MFA_ENROLMENT_REQUIRED" && mfaEnrolmentStart) {
        navigate(
          buildPath("/auth/mfa-enrolment", {
            email: nextEmail.trim(),
            ...(invitationToken ? { invitationToken } : {}),
          }),
          {
            state: mfaEnrolmentStart,
          },
        );
        return;
      }

      // Multiple organisations detected for the auth user.
      if (parsedError.code === "ORGANISATION_SELECTION_REQUIRED" && (parsedError.organisations ?? []).length > 1) {
        navigate(buildPath("/auth/select-organisation", invitationToken ? { invitationToken } : undefined), {
          state: {
            organisation: parsedError.organisations,
            pendingAuthenticationToken: parsedError.pendingAuthenticationToken ?? "",
          },
        });
        return;
      }

      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    if (invitationToken) {
      navigate(buildPath("/auth/invite/accept", { invitationToken }));
      return;
    }

    completeAuthNavigation(result.payload);
  };

  const handleResend = async () => {
    if (!email.trim()) {
      return;
    }

    const result = await dispatch(
      authV2MagicSend({
        email: email.trim(),
        ...(invitationToken ? { invitationToken } : {}),
      }),
    );

    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);
      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }

    dispatch(
      openSnackbar({
        message: "A new code has been sent.",
        intent: "success",
      }),
    );
  };

  const formik = useFormik({
    initialValues: {
      code: Array(6).fill("") as string[],
    },
    onSubmit: (values) => {
      submitMagicCode(email, values.code);
    },
    validationSchema: yup.object({
      code: yup
        .array()
        .of(yup.string())
        .min(6)
        .test(
          "all-filled",
          "Please enter the full 6-digit code.",
          (value) =>
            Array.isArray(value) && value.length === 6 && value.every((v) => typeof v === "string" && v.trim() !== ""),
        ),
    }),
    validateOnChange: false,
    validateOnBlur: false,
  });

  useEffect(() => {
    const deepLinkedEmail = searchParams.get("email");
    const deepLinkedCode = searchParams.get("code");
    if (!deepLinkedEmail || !deepLinkedCode || autoSubmittedRef.current) {
      return;
    }

    autoSubmittedRef.current = true;
    submitMagicCode(deepLinkedEmail, deepLinkedCode);
  }, [searchParams]);

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">Check your email</Typography>
      </Stack>
      <AuthCard onSubmit={() => formik.handleSubmit()}>
        <Stack rowGap={4}>
          <Alert
            intent="neutral"
            variant="filled"
            description="You'll receive an email with a code if you have an account"
          />
          <Stack rowGap={4} mb={1}>
            <Stack flexDirection="row" gap={2} alignItems="flex-start">
              <StepIndicator color="primary" size="sm" variant="solid">
                1
              </StepIndicator>
              <Stack>
                <Typography level="body-lg" bold lineHeight={1}>
                  Email code
                </Typography>
                {!!email && <Typography level="body-lg">Sent to {email}</Typography>}
              </Stack>
            </Stack>
            <FormControl error={!!formik.errors.code}>
              <PinInput
                intent={formik.errors.code ? "danger" : undefined}
                length={6}
                onChange={(value) => {
                  formik.setFieldValue("code", value);
                  if (formik.errors.code) {
                    formik.setFieldError("code", undefined);
                  }
                }}
                onComplete={(value) => {
                  formik.setFieldValue("code", value.split(""));
                  if (formik.errors.code) {
                    formik.setFieldError("code", undefined);
                  }
                  submitMagicCode(email, value);
                }}
                autoFocus
                size="lg"
                value={formik.values.code}
                gap={2.5}
              />
              {formik.errors.code ? (
                <FormHelperText>
                  <ErrorOutlineIcon sx={{ fontSize: "1rem" }} />
                  {formik.errors.code}
                </FormHelperText>
              ) : null}
            </FormControl>
          </Stack>
          {!!email ? (
            <Stack flexDirection="row" columnGap={1}>
              <Typography level="body-sm" textColor="neutral.600">
                Didn't receive the email?
              </Typography>
              {sendLoading ? (
                <Typography level="body-sm" textColor="primary.200" bold>
                  Resending...
                </Typography>
              ) : (
                <>
                  <Button variant="inline" disabled={magicOtpCooldownActive} onClick={handleResend}>
                    Resend code
                  </Button>
                  {magicOtpCooldownActive ? (
                    <Typography level="body-sm" textColor="neutral.600">
                      is available in {magicOtpCooldownRemainingSeconds} {magicOtpCooldownUnit}.
                    </Typography>
                  ) : null}
                </>
              )}
            </Stack>
          ) : undefined}
          <Button
            size="lg"
            variant="primary"
            loading={verifyLoading}
            endDecorator={<ChevronRightIcon sx={{ fontSize: "1.2rem" }} />}
            type="submit"
          >
            Verify
          </Button>
        </Stack>
      </AuthCard>
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={navigateToSignIn}
      >
        Go back
      </Button>
    </>
  );
};

export { AuthMagic };
